import { useMemo } from 'react';
import LinkIcon from '@mui/icons-material/Link';
import MouseIcon from '@mui/icons-material/Mouse';
import SearchIcon from '@mui/icons-material/Search';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Select, MenuItem, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { Link } from 'react-router-dom';
import {
  DataTable,
  MetricCard,
  PageHeader,
  SectionCard,
  LoadingState,
} from '@/components/shared';
import { SearchConsoleSummary, SearchAnalyticsRow, SearchConsoleProperty } from '@/services/searchConsole';
import { Column } from '@/trio-ui/DataTable';

export type DatePreset = '7d' | '28d' | '3m' | '6m' | '12m';

type Props = {
  isConnected: boolean;
  siteUrl: string;
  availableSites: SearchConsoleProperty[];
  onSiteChange: (siteUrl: string) => void;
  summary: SearchConsoleSummary;
  isLoading: boolean;
  datePreset: DatePreset;
  onDatePresetChange: (preset: DatePreset) => void;
};

const num = (n: number): string => n.toLocaleString();
const pct = (n: number): string => `${(n * 100).toFixed(1)}%`;

const queryColumns: Column<SearchAnalyticsRow>[] = [
  {
    id: 'query',
    label: 'Keyword',
    render: (row) => (
      <Box sx={{ fontWeight: 500, fontSize: 13 }}>{row.keys?.[0] ?? '—'}</Box>
    ),
  },
  {
    id: 'clicks',
    label: 'Clicks',
    align: 'right',
    render: (row) => num(row.clicks),
  },
  {
    id: 'impressions',
    label: 'Impressions',
    align: 'right',
    render: (row) => num(row.impressions),
  },
  {
    id: 'ctr',
    label: 'CTR',
    align: 'right',
    render: (row) => pct(row.ctr),
  },
  {
    id: 'position',
    label: 'Avg Position',
    align: 'right',
    render: (row) => {
      let posColor = '#71717A';
      if (row.position <= 10) posColor = '#16A34A';
      else if (row.position <= 20) posColor = '#D97706';
      return (
        <Box sx={{ fontWeight: 500, color: posColor }}>
          {row.position.toFixed(1)}
        </Box>
      );
    },
  },
];

const pageColumns: Column<SearchAnalyticsRow>[] = [
  {
    id: 'page',
    label: 'Page',
    render: (row) => {
      const url = row.keys?.[0] ?? '';
      const path = url.replace(/^https?:\/\/[^/]+/, '') || '/';
      return (
        <Box sx={{ fontSize: 13 }}>
          <Box sx={{ fontWeight: 500 }}>{path}</Box>
          <Box sx={{ fontSize: 11, color: '#71717A', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {url}
          </Box>
        </Box>
      );
    },
  },
  {
    id: 'clicks',
    label: 'Clicks',
    align: 'right',
    render: (row) => num(row.clicks),
  },
  {
    id: 'impressions',
    label: 'Impressions',
    align: 'right',
    render: (row) => num(row.impressions),
  },
  {
    id: 'ctr',
    label: 'CTR',
    align: 'right',
    render: (row) => pct(row.ctr),
  },
];

const CEOxSEOComponent = function ({
  isConnected,
  siteUrl,
  availableSites,
  onSiteChange,
  summary,
  isLoading,
  datePreset,
  onDatePresetChange,
}: Props): JSX.Element {
  const { current, previous, topQueries, topPages, dailyTrend } = summary;

  // --- Traffic trend chart ---
  const trendOption = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) =>
          params.map((p: any) => `${p.marker} ${p.seriesName}: ${num(p.value)}`).join('<br/>'),
      },
      legend: {
        data: ['Clicks', 'Impressions'],
        bottom: 0,
        textStyle: { fontSize: 12, color: '#71717A' },
      },
      grid: { left: 55, right: 55, top: 20, bottom: 44 },
      xAxis: {
        type: 'category',
        data: dailyTrend.map((r) => {
          const d = r.keys?.[0] ?? '';
          return d.slice(5); // MM-DD
        }),
        axisLabel: { fontSize: 10, color: '#71717A', rotate: 45 },
        axisLine: { lineStyle: { color: '#E4E4E7' } },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Clicks',
          axisLabel: { fontSize: 11, color: '#71717A' },
          splitLine: { lineStyle: { color: '#F4F4F5' } },
        },
        {
          type: 'value',
          name: 'Impressions',
          axisLabel: { fontSize: 11, color: '#71717A' },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: 'Clicks',
          type: 'line',
          data: dailyTrend.map((r) => r.clicks),
          lineStyle: { color: '#054AD9', width: 2 },
          itemStyle: { color: '#054AD9' },
          symbol: 'none',
          smooth: true,
          areaStyle: { color: 'rgba(5,74,217,0.08)' },
        },
        {
          name: 'Impressions',
          type: 'line',
          yAxisIndex: 1,
          data: dailyTrend.map((r) => r.impressions),
          lineStyle: { color: '#8B5CF6', width: 2 },
          itemStyle: { color: '#8B5CF6' },
          symbol: 'none',
          smooth: true,
          areaStyle: { color: 'rgba(139,92,246,0.08)' },
        },
      ],
    }),
    [dailyTrend]
  );

  if (!isConnected) {
    return (
      <>
        <PageHeader title="SEO" />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            borderRadius: '8px',
            backgroundColor: '#FEF3C7',
            border: '1px solid #FDE68A',
          }}
        >
          <LinkIcon sx={{ fontSize: 18, color: '#D97706' }} />
          <Box sx={{ fontSize: 13, color: '#92400E', flex: 1 }}>
            Connect your <strong>Google</strong> account to track organic search performance.
          </Box>
          <Link
            to="/settings/integrations"
            style={{ fontSize: 13, color: '#054AD9', textDecoration: 'none', fontWeight: 500 }}
          >
            Connect now
          </Link>
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="SEO" />
        <LoadingState message="Loading search data..." />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="SEO"
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={datePreset}
              exclusive
              onChange={(_, val) => val && onDatePresetChange(val)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: 13,
                  px: 1.5,
                  py: 0.5,
                  borderColor: '#E4E4E7',
                  '&.Mui-selected': {
                    backgroundColor: '#054AD915',
                    color: '#054AD9',
                    borderColor: '#054AD9',
                    '&:hover': { backgroundColor: '#054AD925' },
                  },
                },
              }}
            >
              <ToggleButton value="7d">7 days</ToggleButton>
              <ToggleButton value="28d">28 days</ToggleButton>
              <ToggleButton value="3m">3 months</ToggleButton>
              <ToggleButton value="6m">6 months</ToggleButton>
              <ToggleButton value="12m">12 months</ToggleButton>
            </ToggleButtonGroup>
            {availableSites.length > 1 && (
              <Select
                value={siteUrl}
                onChange={(e) => onSiteChange(e.target.value)}
                size="small"
                variant="outlined"
                sx={{
                  minWidth: 200,
                  fontSize: 13,
                  '& .MuiSelect-select': { py: 0.75, px: 1.5 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E4E4E7' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#054AD9' },
                }}
              >
                {availableSites.map((site) => (
                  <MenuItem key={site.siteUrl} value={site.siteUrl} sx={{ fontSize: 13 }}>
                    {site.siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </MenuItem>
                ))}
              </Select>
            )}
            {availableSites.length <= 1 && (
              <Box sx={{ fontSize: 12, color: '#71717A' }}>
                {siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </Box>
            )}
          </Box>
        }
      />

      {/* KPI Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <MetricCard
          label="Clicks"
          value={num(current.totalClicks)}
          subtitle={`vs ${num(previous.totalClicks)} prev period`}
          color="#054AD9"
          indicator={<MouseIcon sx={{ fontSize: 16 }} />}
        />
        <MetricCard
          label="Impressions"
          value={num(current.totalImpressions)}
          subtitle={`vs ${num(previous.totalImpressions)} prev period`}
          color="#8B5CF6"
          indicator={<VisibilityIcon sx={{ fontSize: 16 }} />}
        />
        <MetricCard
          label="Avg CTR"
          value={pct(current.avgCtr)}
          subtitle={`vs ${pct(previous.avgCtr)} prev period`}
          color="#10B981"
          indicator={<TouchAppIcon sx={{ fontSize: 16 }} />}
        />
        <MetricCard
          label="Avg Position"
          value={current.avgPosition.toFixed(1)}
          subtitle={`vs ${previous.avgPosition.toFixed(1)} prev period`}
          color={current.avgPosition <= 20 ? '#054AD9' : '#D97706'}
          indicator={<SearchIcon sx={{ fontSize: 16 }} />}
        />
      </Box>

      {/* Traffic trend */}
      <Box sx={{ mb: 3 }}>
        <SectionCard title="Organic Traffic Trend" subtitle="Daily clicks and impressions">
          {dailyTrend.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', fontSize: 13, color: '#71717A' }}>
              No data available.
            </Box>
          ) : (
            <ReactECharts option={trendOption} style={{ height: 300 }} />
          )}
        </SectionCard>
      </Box>

      {/* Top Keywords + Top Pages */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        <SectionCard title="Top Keywords" subtitle="By clicks, last 28 days">
          <DataTable
            columns={queryColumns}
            data={topQueries}
            rowKey={(r) => r.keys?.[0] ?? String(Math.random())}
            emptyMessage="No keyword data."
          />
        </SectionCard>

        <SectionCard title="Top Pages" subtitle="By clicks, last 28 days">
          <DataTable
            columns={pageColumns}
            data={topPages}
            rowKey={(r) => r.keys?.[0] ?? String(Math.random())}
            emptyMessage="No page data."
          />
        </SectionCard>
      </Box>
    </>
  );
};

export default CEOxSEOComponent;
